<template>
  <v-card outlined class="mb-4 contract-card">
    <v-card-text>
      <v-row align="center" class="mb-3">
        <v-col cols="auto">
          <v-icon color="primary darken-2" size="32">mdi-file-document-outline</v-icon>
        </v-col>
        <v-col>
          <div class="text-h6 font-weight-bold primary--text text--darken-2">
            ข้อมูลการทำสัญญาจ้าง
          </div>
        </v-col>
      </v-row>

      <v-row>
        <!-- คำสั่งที่ -->
        <v-col cols="12" md="6">
          <v-text-field
            v-model="localData.order_app_now"
            outlined
            dense
            label="คำสั่งที่"
            prepend-inner-icon="mdi-file-document-edit"
            placeholder="ระบุเลขที่คำสั่ง"
            required
            :rules="orderRules"
            hint="เลขที่คำสั่งจ้าง เช่น ศธ 1234/2567"
            persistent-hint
          >
            <template v-slot:append>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    small
                    color="info"
                  >
                    mdi-information
                  </v-icon>
                </template>
                <span>เลขที่คำสั่งการจ้างงาน</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>

        <!-- วันที่เริ่มสัญญา -->
        <v-col cols="12" md="6">
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="localData.date_app_now"
                outlined
                dense
                label="วันที่เริ่มสัญญา"
                prepend-inner-icon="mdi-calendar-start"
                readonly
                v-bind="attrs"
                v-on="on"
                required
                :rules="startDateRules"
                placeholder="เลือกวันที่เริ่มสัญญา"
                hint="วันที่เริ่มต้นการจ้างงาน"
                persistent-hint
              >
                <template v-slot:append>
                  <v-icon color="success">mdi-calendar-check</v-icon>
                </template>
              </v-text-field>
            </template>
            <v-date-picker
              v-model="localData.date_app_now"
              no-title
              scrollable
              locale="th"
              :max="maxStartDate"
              @input="startDateMenu = false"
            >
              <v-spacer />
              <v-btn text color="primary" @click="startDateMenu = false">
                ปิด
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>

        <!-- วันที่สิ้นสุดสัญญา -->
        <v-col cols="12" md="6">
          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="localData.date_end_govem"
                outlined
                dense
                label="วันที่สิ้นสุดสัญญา"
                prepend-inner-icon="mdi-calendar-end"
                readonly
                v-bind="attrs"
                v-on="on"
                required
                :rules="endDateRules"
                placeholder="เลือกวันที่สิ้นสุดสัญญา"
                :hint="contractDurationHint"
                :persistent-hint="true"
                :error="isContractExpired"
              >
                <template v-slot:append>
                  <v-icon :color="contractStatusColor">
                    {{ contractStatusIcon }}
                  </v-icon>
                </template>
              </v-text-field>
            </template>
            <v-date-picker
              v-model="localData.date_end_govem"
              no-title
              scrollable
              locale="th"
              :min="minEndDate"
              @input="endDateMenu = false"
            >
              <v-spacer />
              <v-btn text color="primary" @click="endDateMenu = false">
                ปิด
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>

        <!-- แสดงระยะเวลาสัญญา -->
        <v-col cols="12" md="6" v-if="contractDuration">
          <v-alert
            dense
            outlined
            type="info"
            icon="mdi-clock-outline"
            class="mb-0"
          >
            <div class="d-flex align-center">
              <v-icon left>mdi-calendar-range</v-icon>
              <div>
                <div class="font-weight-bold">ระยะเวลาสัญญา</div>
                <div class="text-caption">{{ contractDuration }}</div>
              </div>
            </div>
          </v-alert>
        </v-col>

        <!-- ไฟล์สัญญาจ้าง -->
        <v-col cols="12">
          <v-divider class="my-3" />
          
          <div class="text-subtitle-2 font-weight-bold mb-3">
            <v-icon left color="primary darken-2">mdi-file-pdf-box</v-icon>
            ไฟล์สัญญาจ้างพนักงานราชการ
          </div>

          <!-- แสดงไฟล์ที่มีอยู่แล้ว -->
          <div v-if="localData.govem_contract" class="file-display-section">
            <v-alert
              dense
              outlined
              type="success"
              icon="mdi-file-check"
              prominent
            >
              <v-row align="center">
                <v-col cols="12" sm="8">
                  <div class="font-weight-bold mb-1">
                    <v-icon small left>mdi-check-circle</v-icon>
                    สัญญาจ้างอัปโหลดแล้ว
                  </div>
                  <div class="text-caption d-flex align-center">
                    <v-icon small class="mr-1">mdi-file-pdf-box</v-icon>
                    <span class="font-weight-medium">{{ localData.govem_contract }}</span>
                  </div>
                  <div class="text-caption grey--text mt-1">
                    <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                    อัปโหลดเมื่อ: {{ uploadedDate }}
                  </div>
                </v-col>
                <v-col cols="12" sm="4" class="text-right">
                  <v-btn
                    small
                    outlined
                    color="info"
                    class="mr-2 mb-2 mb-sm-0"
                    @click="$emit('view')"
                  >
                    <v-icon small left>mdi-eye</v-icon>
                    ดูไฟล์
                  </v-btn>
                  <v-btn
                    small
                    outlined
                    color="error"
                    @click="confirmDelete"
                  >
                    <v-icon small left>mdi-delete</v-icon>
                    ลบ
                  </v-btn>
                </v-col>
              </v-row>
            </v-alert>
          </div>

          <!-- อัปโหลดไฟล์ใหม่ -->
          <div v-else class="file-upload-section">
            <v-file-input
              v-model="contractFile"
              accept="application/pdf"
              outlined
              dense
              label="เลือกไฟล์สัญญาจ้าง"
              prepend-icon="mdi-paperclip"
              prepend-inner-icon="mdi-file-pdf-box"
              :show-size="1000"
              :rules="fileRules"
              placeholder="คลิกเพื่อเลือกไฟล์ PDF"
              hint="อัปโหลดไฟล์สัญญาจ้างพนักงานราชการ (PDF เท่านั้น, ขนาดไม่เกิน 10 MB)"
              persistent-hint
              @change="handleFileChange"
            >
              <template v-slot:selection="{ text }">
                <v-chip
                  small
                  color="primary darken-2"
                  text-color="white"
                  class="ma-1"
                >
                  <v-icon left small>mdi-file-pdf-box</v-icon>
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>

            <!-- แสดงข้อมูลไฟล์ที่เลือก -->
            <v-fade-transition>
              <v-card v-if="contractFile" outlined class="mt-3 file-preview-card">
                <v-card-text>
                  <v-row align="center">
                    <v-col cols="auto">
                      <v-avatar color="primary lighten-4" size="48">
                        <v-icon color="primary darken-2">mdi-file-pdf-box</v-icon>
                      </v-avatar>
                    </v-col>
                    <v-col>
                      <div class="font-weight-bold">{{ contractFile.name }}</div>
                      <div class="text-caption grey--text">
                        ขนาด: {{ fileSize }} | ประเภท: PDF
                      </div>
                    </v-col>
                    <v-col cols="auto">
                      <v-chip small color="success" outlined>
                        <v-icon small left>mdi-check</v-icon>
                        พร้อมอัปโหลด
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-fade-transition>

            <!-- คำแนะนำการอัปโหลด -->
            <v-expansion-panels flat class="mt-3">
              <v-expansion-panel>
                <v-expansion-panel-header color="primary lighten-5">
                  <div>
                    <v-icon small left color="primary darken-2">mdi-information-outline</v-icon>
                    <span class="text-caption font-weight-bold">
                      คำแนะนำและข้อกำหนดในการอัปโหลดไฟล์สัญญา
                    </span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="text-subtitle-2 font-weight-bold mb-2">
                        ✅ ข้อกำหนด
                      </div>
                      <v-list dense>
                        <v-list-item>
                          <v-list-item-icon>
                            <v-icon small color="success">mdi-check-circle</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="text-caption">
                              ไฟล์ต้องเป็นรูปแบบ PDF เท่านั้น
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-icon>
                            <v-icon small color="success">mdi-check-circle</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="text-caption">
                              ขนาดไฟล์ไม่เกิน 10 MB
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-icon>
                            <v-icon small color="success">mdi-check-circle</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="text-caption">
                              ไฟล์ต้องสแกนชัดเจน อ่านง่าย
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="text-subtitle-2 font-weight-bold mb-2">
                        📋 เอกสารที่ต้องมีในสัญญา
                      </div>
                      <v-list dense>
                        <v-list-item>
                          <v-list-item-icon>
                            <v-icon small color="info">mdi-file-document</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="text-caption">
                              เลขที่สัญญา และวันที่ทำสัญญา
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-icon>
                            <v-icon small color="info">mdi-file-document</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="text-caption">
                              ชื่อ-นามสกุล พนักงานราชการ
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-icon>
                            <v-icon small color="info">mdi-file-document</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="text-caption">
                              ลายเซ็นผู้มีอำนาจและตราประทับ
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "ContractSection",
  
  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      startDateMenu: false,
      endDateMenu: false,
      contractFile: null,

      // กฎการตรวจสอบ
      orderRules: [
        v => !!v || "กรุณากรอกเลขที่คำสั่ง",
        v => (v && v.length >= 3) || "เลขที่คำสั่งต้องมีอย่างน้อย 3 ตัวอักษร",
        v => (v && v.length <= 100) || "เลขที่คำสั่งต้องไม่เกิน 100 ตัวอักษร"
      ],

      startDateRules: [
        v => !!v || "กรุณาเลือกวันที่เริ่มสัญญา",
        v => this.validateStartDate(v) || "วันที่เริ่มสัญญาไม่ถูกต้อง"
      ],

      endDateRules: [
        v => !!v || "กรุณาเลือกวันที่สิ้นสุดสัญญา",
        v => this.validateEndDate(v) || "วันที่สิ้นสุดต้องมากกว่าวันที่เริ่มสัญญา",
      ],

      fileRules: [
        v => !v || v.size < 10000000 || "ขนาดไฟล์ต้องไม่เกิน 10 MB",
        v => !v || v.type === "application/pdf" || "รองรับเฉพาะไฟล์ PDF เท่านั้น",
        v => !v || v.name.toLowerCase().endsWith('.pdf') || "ไฟล์ต้องมีนามสกุล .pdf"
      ]
    };
  },

  computed: {
    localData: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },

    // วันที่สูงสุดสำหรับวันเริ่มสัญญา (ไม่เกิน 1 ปีในอนาคต)
    maxStartDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() + 1);
      return date.toISOString().substr(0, 10);
    },

    // วันที่ต่ำสุดสำหรับวันสิ้นสุดสัญญา
    minEndDate() {
      if (!this.localData.date_app_now) return this.currentDate;
      const startDate = new Date(this.localData.date_app_now);
      startDate.setDate(startDate.getDate() + 1); // อย่างน้อย 1 วันหลังวันเริ่มสัญญา
      return startDate.toISOString().substr(0, 10);
    },

    // วันที่ปัจจุบัน
    currentDate() {
      return new Date().toISOString().substr(0, 10);
    },

    // คำนวณระยะเวลาสัญญา
    contractDuration() {
      if (!this.localData.date_app_now || !this.localData.date_end_govem) return null;
      
      const start = new Date(this.localData.date_app_now);
      const end = new Date(this.localData.date_end_govem);
      
      if (end <= start) return null;

      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);
      const days = diffDays % 30;

      let result = [];
      if (years > 0) result.push(`${years} ปี`);
      if (months > 0) result.push(`${months} เดือน`);
      if (days > 0) result.push(`${days} วัน`);

      return result.join(' ');
    },

    // คำแนะนำระยะเวลาสัญญา
    contractDurationHint() {
      if (this.isContractExpired) return "⚠️ สัญญาหมดอายุแล้ว";
      if (this.isContractExpiringSoon) {
        return `⚠️ สัญญาใกล้หมดอายุ (เหลือ ${this.daysUntilExpiry} วัน)`;
      }
      return this.contractDuration ? `ระยะเวลาสัญญา: ${this.contractDuration}` : "เลือกวันที่สิ้นสุดสัญญา";
    },

    // ตรวจสอบสัญญาหมดอายุ
    isContractExpired() {
      if (!this.localData.date_end_govem) return false;
      return new Date(this.localData.date_end_govem) < new Date();
    },

    // ตรวจสอบสัญญาใกล้หมดอายุ (30 วัน)
    isContractExpiringSoon() {
      if (!this.localData.date_end_govem) return false;
      const endDate = new Date(this.localData.date_end_govem);
      const today = new Date();
      const diffDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      return diffDays > 0 && diffDays <= 30;
    },

    // จำนวนวันจนกว่าจะหมดอายุ
    daysUntilExpiry() {
      if (!this.localData.date_end_govem) return 0;
      const endDate = new Date(this.localData.date_end_govem);
      const today = new Date();
      return Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    },

    // สีสถานะสัญญา
    contractStatusColor() {
      if (this.isContractExpired) return "error";
      if (this.isContractExpiringSoon) return "warning";
      return "success";
    },

    // ไอคอนสถานะสัญญา
    contractStatusIcon() {
      if (this.isContractExpired) return "mdi-alert-circle";
      if (this.isContractExpiringSoon) return "mdi-alert";
      return "mdi-check-circle";
    },

    // ขนาดไฟล์ที่แสดง
    fileSize() {
      if (!this.contractFile) return "";
      const size = this.contractFile.size;
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    },

    // วันที่อัปโหลด (จำลอง)
    uploadedDate() {
      return new Date().toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  },

  methods: {
    // ตรวจสอบวันที่เริ่มสัญญา
    validateStartDate(value) {
      if (!value) return true;
      const startDate = new Date(value);
      const maxDate = new Date();
      maxDate.setFullYear(maxDate.getFullYear() + 1);
      return startDate <= maxDate;
    },

    // ตรวจสอบวันที่สิ้นสุดสัญญา
    validateEndDate(value) {
      if (!value || !this.localData.date_app_now) return true;
      return new Date(value) > new Date(this.localData.date_app_now);
    },

    // ตรวจสอบระยะเวลาสัญญา
    validateContractDuration(value) {
      if (!value || !this.localData.date_app_now) return true;
      
      const start = new Date(this.localData.date_app_now);
      const end = new Date(value);
      const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      
      // ตรวจสอบระยะเวลา: อย่างน้อย 1 เดือน (30 วัน) และไม่เกิน 5 ปี (1825 วัน)
      return diffDays >= 30 && diffDays <= 1825;
    },

    // จัดการการเปลี่ยนแปลงไฟล์
    handleFileChange(file) {
      if (file) {
        // ตรวจสอบขนาดไฟล์
        if (file.size > 10000000) {
          this.$emit("file-error", "ขนาดไฟล์ต้องไม่เกิน 10 MB");
          this.contractFile = null;
          return;
        }

        // ตรวจสอบประเภทไฟล์
        if (file.type !== "application/pdf") {
          this.$emit("file-error", "รองรับเฉพาะไฟล์ PDF เท่านั้น");
          this.contractFile = null;
          return;
        }

        // ตรวจสอบนามสกุลไฟล์
        if (!file.name.toLowerCase().endsWith('.pdf')) {
          this.$emit("file-error", "ไฟล์ต้องมีนามสกุล .pdf");
          this.contractFile = null;
          return;
        }

        this.$emit("file-change", file);
      }
    },

    // ยืนยันการลบไฟล์
    async confirmDelete() {
      const result = await Swal.fire({
        title: "ยืนยันการลบไฟล์สัญญา",
        html: `
          <div class="text-left">
            <p>คุณต้องการลบไฟล์สัญญาจ้างนี้หรือไม่?</p>
            <div class="mt-3 p-3 bg-warning-light rounded">
              <strong>⚠️ คำเตือน:</strong>
              <ul class="mt-2 mb-0">
                <li>การลบไฟล์จะไม่สามารถกู้คืนได้</li>
                <li>คุณจะต้องอัปโหลดไฟล์ใหม่อีกครั้ง</li>
              </ul>
            </div>
          </div>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#b3151b",
        confirmButtonText: '<i class="mdi mdi-delete"></i> ใช่, ลบเลย',
        cancelButtonText: '<i class="mdi mdi-close"></i> ยกเลิก',
        customClass: {
          confirmButton: 'v-btn v-btn--outlined theme--light error--text',
          cancelButton: 'v-btn v-btn--outlined theme--light primary--text'
        }
      });

      if (result.isConfirmed) {
        this.$emit("delete");
      }
    }
  }
};
</script>

<style scoped>
.contract-card {
  transition: all 0.3s ease;
  border-radius: 12px;
  border: 2px solid #fbecec;
}

.contract-card:hover {
  box-shadow: 0 6px 24px rgba(156, 39, 176, 0.15);
  border-color: #d9777b;
}

.file-display-section,
.file-upload-section {
  padding: 8px 0;
}

.file-preview-card {
  border-radius: 12px;
  background: linear-gradient(to right, #fbecec, #fbecec);
  border: 2px dashed #b3151b;
  transition: all 0.3s ease;
}

.file-preview-card:hover {
  border-color: #b3151b;
  box-shadow: 0 4px 12px rgba(156, 39, 176, 0.2);
}

/* Custom styling */
::v-deep .v-text-field--outlined {
  border-radius: 8px;
}

::v-deep .v-input__icon--prepend-inner i {
  color: #b3151b !important;
}

::v-deep .v-alert {
  border-radius: 12px;
}

::v-deep .v-expansion-panel {
  border-radius: 8px !important;
  border: 1px solid #d9777b;
}

::v-deep .v-expansion-panel-header {
  border-radius: 8px !important;
}

::v-deep .v-chip {
  border-radius: 8px !important;
}

/* Animation */
.v-fade-transition-enter-active,
.v-fade-transition-leave-active {
  transition: opacity 0.3s ease;
}

.v-fade-transition-enter,
.v-fade-transition-leave-to {
  opacity: 0;
}
</style>